import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [worlds, setWorlds] = useState([]);
  const [stats, setStats] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [adminTabs, setAdminTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Neue Welt Dialog
  const [showWorldDialog, setShowWorldDialog] = useState(false);
  const [newWorld, setNewWorld] = useState({
    name: '',
    id: '',
    key: '',
    enabled: true
  });
  const [syncing, setSyncing] = useState({});

  // Welt-Zuweisung Dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedWorldId, setSelectedWorldId] = useState('');

  // Spieler-Zuweisung Dialog
  const [showPlayerAssignDialog, setShowPlayerAssignDialog] = useState(false);
  const [selectedUserForPlayer, setSelectedUserForPlayer] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [availableTribes, setAvailableTribes] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [selectedTribeId, setSelectedTribeId] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');

  useEffect(() => {
    switch (activeTab) {
      case 'users':
        loadUsers();
        break;
      case 'worlds':
        loadWorlds();
        break;
      case 'stats':
        loadStats();
        break;
      case 'logs':
        loadSyncLogs();
        break;
      case 'tabs':
        loadAdminTabs();
        break;
    }
  }, [activeTab]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/users');
      setUsers(response.data.data.users);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWorlds = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/worlds');
      setWorlds(response.data.data.worlds);
    } catch (error) {
      console.error('Fehler beim Laden der Welten:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSyncLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/sync-logs');
      setSyncLogs(response.data.data.logs);
    } catch (error) {
      console.error('Fehler beim Laden der Sync-Logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminTabs = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/tabs');
      setAdminTabs(response.data.data.tabs);
    } catch (error) {
      console.error('Fehler beim Laden der Admin-Tabs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayersInWorld = async (world) => {
    try {
      const [playersRes, tribesRes] = await Promise.all([
        axios.get(`/admin/players/${world}?search=${playerSearch}`),
        axios.get(`/admin/tribes/${world}`)
      ]);
      setAvailablePlayers(playersRes.data.data.players);
      setAvailableTribes(tribesRes.data.data.tribes);
    } catch (error) {
      console.error('Fehler beim Laden der Spieler:', error);
    }
  };

  const handleClearAllData = async () => {
    if (!window.confirm('ACHTUNG: Alle SOS, Truppen-Dumps und Tabs werden unwiderruflich gelöscht. Sind Sie sicher?')) {
      return;
    }
    
    try {
      const response = await axios.delete('/admin/data/clear');
      setMessage(response.data.message);
      setMessageType('success');
      
      // Reload data
      if (activeTab === 'stats') loadStats();
      if (activeTab === 'tabs') loadAdminTabs();
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen der Daten';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      const response = await axios.post(`/admin/users/${userId}/${action}`);
      setMessage(response.data.message);
      setMessageType('success');
      loadUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || `Fehler bei ${action}`;
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleWorldSync = async (worldId) => {
    try {
      setSyncing(prev => ({ ...prev, [worldId]: true }));
      const response = await axios.post(`/admin/worlds/${worldId}/sync`);
      setMessage(response.data.message);
      setMessageType('success');
      loadWorlds();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Sync';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setSyncing(prev => ({ ...prev, [worldId]: false }));
    }
  };

  const handleCreateWorld = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/admin/worlds', newWorld);
      setMessage(response.data.message);
      setMessageType('success');
      setShowWorldDialog(false);
      setNewWorld({ name: '', id: '', key: '', enabled: true });
      loadWorlds();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Erstellen der Welt';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleDeleteWorld = async (worldId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Welt löschen möchten?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`/admin/worlds/${worldId}`);
      setMessage(response.data.message);
      setMessageType('success');
      loadWorlds();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen der Welt';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleAssignWorld = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/admin/users/${selectedUser._id}/assign-world`, {
        worldId: selectedWorldId
      });
      setMessage(response.data.message);
      setMessageType('success');
      setShowAssignDialog(false);
      setSelectedUser(null);
      setSelectedWorldId('');
      loadUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Zuweisen der Welt';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleAssignPlayer = async (e) => {
    e.preventDefault();
    if (!selectedPlayerId) {
      setMessage('Bitte wählen Sie einen Spieler aus');
      setMessageType('error');
      return;
    }

    try {
      const response = await axios.post(`/admin/users/${selectedUserForPlayer._id}/assign-player`, {
        playerId: selectedPlayerId,
        tribeId: selectedTribeId || null
      });
      setMessage(response.data.message);
      setMessageType('success');
      setShowPlayerAssignDialog(false);
      setSelectedUserForPlayer(null);
      setSelectedPlayerId('');
      setSelectedTribeId('');
      setPlayerSearch('');
      setAvailablePlayers([]);
      setAvailableTribes([]);
      loadUsers();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Zuweisen des Spielers';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const openAssignDialog = (user) => {
    setSelectedUser(user);
    setShowAssignDialog(true);
  };

  const openPlayerAssignDialog = (user) => {
    setSelectedUserForPlayer(user);
    setShowPlayerAssignDialog(true);
    if (user.world) {
      loadPlayersInWorld(user.world);
    }
  };

  const tabs = [
    { id: 'users', name: 'Benutzer', icon: '👥' },
    { id: 'worlds', name: 'Welten', icon: '🌍' },
    { id: 'stats', name: 'Statistiken', icon: '📊' },
    { id: 'tabs', name: 'Tabs', icon: '📋' },
    { id: 'logs', name: 'Sync-Logs', icon: '📝' }
  ];

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const formatCoords = (coords) => {
    return `${coords.x}|${coords.y}`;
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-full shadow-lg mb-4">
            <span className="text-2xl text-white">👑</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Admin-Dashboard
          </h1>
          <p className="text-xl text-gray-600">Systemverwaltung für Die Stämme Defense</p>
        </div>

        {/* Status Message */}
        {message && (
          <div className={`px-6 py-4 rounded-lg border ${
            messageType === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
            messageType === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
            'bg-blue-50 border-blue-200 text-blue-700'
          }`}>
            <div className="font-medium">{message}</div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="card">
          <div className="flex flex-wrap justify-center gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="text-lg mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="card text-center py-16">
            <div className="spinner mb-4 mx-auto"></div>
            <span className="text-lg text-gray-600">Lade Daten...</span>
          </div>
        ) : (
          <div className="card">
            
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Benutzerverwaltung</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Name</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">E-Mail</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Welt</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Spieler</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Stamm</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Rolle</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Status</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, index) => (
                        <tr key={user._id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : ''} hover:bg-gray-100`}>
                          <td className="py-3 px-4 font-medium text-gray-800">{user.username}</td>
                          <td className="py-3 px-4 text-gray-600">{user.email}</td>
                          <td className="py-3 px-4">
                            <span className="coords">{user.world || 'Keine'}</span>
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {user.playerId ? (
                              <div>
                                <div className="font-medium">{user.playerId.name}</div>
                                <div className="text-xs text-gray-500">ID: {user.playerId.playerId}</div>
                              </div>
                            ) : (
                              <span className="text-red-500">Nicht zugewiesen</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {user.playerId?.tribeId ? (
                              <div>
                                <div className="font-medium">{user.playerId.tribeId.name}</div>
                                <div className="text-xs text-gray-500">[{user.playerId.tribeId.tag}]</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">Kein Stamm</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              user.role === 'superadmin' ? 'bg-red-100 text-red-800' :
                              user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {user.isActive ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => openAssignDialog(user)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Welt
                              </button>
                              <button
                                onClick={() => openPlayerAssignDialog(user)}
                                className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                              >
                                Spieler
                              </button>
                              {!user.isActive ? (
                                <button
                                  onClick={() => handleUserAction(user._id, 'activate')}
                                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                                >
                                  Aktivieren
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUserAction(user._id, 'deactivate')}
                                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                                >
                                  Deaktivieren
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Worlds Tab */}
            {activeTab === 'worlds' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Weltenverwaltung</h2>
                  <button
                    onClick={() => setShowWorldDialog(true)}
                    className="btn-success"
                  >
                    Neue Welt
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Name</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Key</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Status</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Letzter Sync</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-4">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {worlds.map((world, index) => (
                        <tr key={world._id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : ''} hover:bg-gray-100`}>
                          <td className="py-3 px-4 font-medium text-gray-800">{world.name}</td>
                          <td className="py-3 px-4">
                            <span className="coords">{world.key}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              world.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {world.enabled ? 'Aktiv' : 'Inaktiv'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-600 text-sm">
                            {world.lastSyncedAt ? formatDateTime(world.lastSyncedAt) : 'Nie'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleWorldSync(world._id)}
                                disabled={syncing[world._id]}
                                className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 text-sm font-medium"
                              >
                                {syncing[world._id] ? 'Läuft...' : 'Sync'}
                              </button>
                              <button
                                onClick={() => handleDeleteWorld(world._id)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Löschen
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && stats && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Systemstatistiken</h2>
                  <button
                    onClick={handleClearAllData}
                    className="btn-danger"
                  >
                    Alle Daten löschen
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-blue-50 rounded-lg p-6 text-center border border-blue-200">
                    <div className="text-3xl mb-2">👥</div>
                    <div className="text-2xl font-bold text-blue-800 mb-1">{stats.users.totalUsers}</div>
                    <div className="text-blue-600 font-medium">Benutzer</div>
                    <div className="text-sm text-blue-500 mt-2">
                      Aktiv: {stats.users.activeUsers} | Admins: {stats.users.adminUsers}
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-6 text-center border border-green-200">
                    <div className="text-3xl mb-2">📋</div>
                    <div className="text-2xl font-bold text-green-800 mb-1">{stats.tabs.totalTabs}</div>
                    <div className="text-green-600 font-medium">Tabs</div>
                    <div className="text-sm text-green-500 mt-2">
                      Bestätigt: {stats.tabs.confirmedTabs} | Ausstehend: {stats.tabs.pendingTabs}
                    </div>
                  </div>
                  
                  <div className="bg-red-50 rounded-lg p-6 text-center border border-red-200">
                    <div className="text-3xl mb-2">🚨</div>
                    <div className="text-2xl font-bold text-red-800 mb-1">{stats.sos.totalSOS}</div>
                    <div className="text-red-600 font-medium">SOS</div>
                    <div className="text-sm text-red-500 mt-2">
                      Angriffe: {stats.sos.totalIncs}
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-6 text-center border border-purple-200">
                    <div className="text-3xl mb-2">⚔️</div>
                    <div className="text-2xl font-bold text-purple-800 mb-1">{stats.troops.totalDumps}</div>
                    <div className="text-purple-600 font-medium">Truppen</div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs Tab */}
            {activeTab === 'tabs' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Alle Tabs ({adminTabs.length})</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-3">Benutzer</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-3">Von</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-3">Nach</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-3">Abfahrt</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-3">Status</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-3">Preset</th>
                        <th className="text-left text-sm font-semibold text-gray-700 py-3 px-3">Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminTabs.map((tab, index) => (
                        <tr key={tab._id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : ''} hover:bg-gray-100`}>
                          <td className="py-3 px-3 text-gray-800">
                            {tab.userId?.username || 'Unbekannt'}
                          </td>
                          <td className="py-3 px-3">
                            <span className="coords">{formatCoords(tab.originCoords)}</span>
                          </td>
                          <td className="py-3 px-3">
                            <span className="coords">{formatCoords(tab.targetCoords)}</span>
                          </td>
                          <td className="py-3 px-3 text-gray-600 text-sm">
                            {formatDateTime(tab.departure)}
                          </td>
                          <td className="py-3 px-3">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              tab.confirmed 
                                ? 'bg-green-100 text-green-800' 
                                : new Date(tab.departure) < new Date()
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {tab.confirmed ? 'Bestätigt' : 
                               new Date(tab.departure) < new Date() ? 'Zu spät' : 'Ausstehend'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-gray-600">{tab.presetName}</td>
                          <td className="py-3 px-3">
                            <a
                              href={tab.tabLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Öffnen
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Logs Tab */}
            {activeTab === 'logs' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Sync-Protokoll</h2>
                <div className="space-y-4">
                  {syncLogs.map((log) => (
                    <div
                      key={log._id}
                      className={`p-4 rounded-lg border ${
                        log.success 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-800">Welt: {log.world}</h3>
                          <p className="text-gray-600 mt-1">{log.message}</p>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <div>{formatDateTime(log.startedAt)}</div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium mt-1 inline-block ${
                            log.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {log.success ? 'Erfolg' : 'Fehler'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dialogs bleiben unverändert, sind schon gut lesbar */}
      {showAssignDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Welt zuweisen: {selectedUser?.username}
            </h3>
            <form onSubmit={handleAssignWorld}>
              <div className="mb-4">
                <label className="form-label">
                  Welt auswählen
                </label>
                <select
                  value={selectedWorldId}
                  onChange={(e) => setSelectedWorldId(e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="">Welt auswählen...</option>
                  {worlds.map((world) => (
                    <option key={world._id} value={world._id}>
                      {world.name} ({world.key})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-2">
                <button type="submit" className="btn-primary flex-1">
                  Zuweisen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignDialog(false);
                    setSelectedUser(null);
                    setSelectedWorldId('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Player Assignment Dialog */}
      {showPlayerAssignDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Spieler zuweisen: {selectedUserForPlayer?.username}
            </h3>
            <form onSubmit={handleAssignPlayer}>
                      <div className="mb-4">
                <label className="form-label">
                  Spieler suchen und auswählen
                </label>
                <input
                  type="text"
                  value={playerSearch}
                  onChange={(e) => {
                    setPlayerSearch(e.target.value);
                    if (selectedUserForPlayer?.world) {
                      loadPlayersInWorld(selectedUserForPlayer.world);
                    }
                  }}
                  className="form-input mb-2"
                  placeholder="Spielername eingeben..."
                />
                {availablePlayers.length > 0 && (
                  <select
                    value={selectedPlayerId}
                    onChange={(e) => setSelectedPlayerId(e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Spieler auswählen...</option>
                    {availablePlayers
                      .filter(player => 
                        player.name.toLowerCase().includes(playerSearch.toLowerCase())
                      )
                      .slice(0, 50) // Limit to 50 results for performance
                      .map((player) => (
                        <option key={player._id} value={player._id}>
                          {player.name} (ID: {player._id}) - {player.points} Punkte
                        </option>
                      ))
                    }
                  </select>
                )}
              </div>
              <div className="mb-4">
                <label className="form-label">
                  Stamm auswählen (optional)
                </label>
                <select
                  value={selectedTribeId}
                  onChange={(e) => setSelectedTribeId(e.target.value)}
                  className="form-input"
                >
                  <option value="">Kein Stamm</option>
                  {availableTribes.map((tribe) => (
                    <option key={tribe._id} value={tribe._id}>
                      {tribe.name} (Tag: {tribe.tag}) - {tribe.points} Punkte
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-2">
                <button type="submit" className="btn-primary flex-1">
                  Zuweisen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPlayerAssignDialog(false);
                    setSelectedUserForPlayer(null);
                    setSelectedPlayerId('');
                    setSelectedTribeId('');
                    setPlayerSearch('');
                    setAvailablePlayers([]);
                    setAvailableTribes([]);
                  }}
                  className="btn-secondary flex-1"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Neue Welt Dialog */}
      {showWorldDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Neue Welt erstellen
            </h3>
            <form onSubmit={handleCreateWorld}>
              <div className="mb-4">
                <label className="form-label">
                  Welt-Name
                </label>
                <input
                  type="text"
                  value={newWorld.name}
                  onChange={(e) => setNewWorld({ ...newWorld, name: e.target.value })}
                  className="form-input"
                  placeholder="z.B. Welt 1"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="form-label">
                  Welt-ID
                </label>
                <input
                  type="text"
                  value={newWorld.id}
                  onChange={(e) => setNewWorld({ ...newWorld, id: e.target.value })}
                  className="form-input"
                  placeholder="z.B. de181"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="form-label">
                  Welt-Key
                </label>
                <input
                  type="text"
                  value={newWorld.key}
                  onChange={(e) => setNewWorld({ ...newWorld, key: e.target.value })}
                  className="form-input"
                  placeholder="z.B. de181"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newWorld.enabled}
                    onChange={(e) => setNewWorld({ ...newWorld, enabled: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="form-label mb-0">Welt aktivieren</span>
                </label>
              </div>
              <div className="flex space-x-2">
                <button type="submit" className="btn-primary flex-1">
                  Erstellen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowWorldDialog(false);
                    setNewWorld({ name: '', id: '', key: '', enabled: true });
                  }}
                  className="btn-secondary flex-1"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Weitere Dialogs... (können bei Bedarf angepasst werden) */}
    </div>
  );
};

export default AdminDashboard;
